import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  isCsvResultsFile,
  isJsonResultsFile,
  isSpreadsheetResultsFile,
  isSupportedResultsFile,
  isXmlResultsFile,
  resolveResultsFileSource,
} from '@/features/dashboard/sections/resultsFileUploadUtils';
import { useResultsFileUploadPolling } from '@/features/dashboard/sections/useResultsFileUploadPolling';
import { useProjectCsvMappingQuery } from '@/features/projects/hooks/useProjectCsvMapping';
import { commitUpload, createUpload, uploadFile } from '@/features/uploads/data';
import { useNotifications } from '@/providers/notificationHooks';
import { getErrorMessage } from '@/shared/data/errors';
import {
  queryKeys,
  gridPaths,
  invalidateGridQuery,
  useAccessToken,
  useInvalidateQueries,
  useMutationErrorSnackbar,
} from '@/shared/hooks';
import { markUploadToastIntent } from '@/shared/notifications/uploadToastIntent';
import { Inline, Link, Text } from '@/ui';
import { defaultCsvHeaderAliases, type CsvHeaderAliases } from '@flowqa/shared';
import type {
  CsvHeaderStatus,
  CsvHeaderValidationResult,
  FileValidationStatus,
  FileValidationResult,
  ResultsFileUploadParams,
  UploadMutationData,
  UploadMutationResult,
} from '@/features/dashboard/sections/useResultsFileUpload.types';

export type ResultsFileUploadState = {
  uploadOpen: boolean;
  uploadProjectId: string;
  uploadTagsInput: string;
  uploadTagValues: string[];
  uploadFiles: File[];
  selectedFiles: Set<string>;
  uploadError: string | null;
  selectedUploadFiles: File[];
  uploadMutation: UploadMutationResult;
  disableUpload: boolean;
  isSupportedFile: (file: File) => boolean;
  csvHeaderStatusByName: Record<string, CsvHeaderStatus | undefined>;
  csvHeaderReasonByName: Record<string, string | null | undefined>;
  fileValidationStatusByName: Record<string, FileValidationStatus | undefined>;
  fileValidationReasonByName: Record<string, string | null | undefined>;
  handleFilesSelected: (files: File[]) => void;
  openUploadDialog: () => void;
  closeUploadDialog: () => void;
  setUploadProjectId: (value: string) => void;
  setUploadTagsInput: (value: string) => void;
  setUploadFiles: (updater: (current: File[]) => File[]) => void;
  setSelectedFiles: (updater: (current: Set<string>) => Set<string>) => void;
  setUploadError: (value: string | null) => void;
  setCsvHeaderStatusByName: (
    updater: (
      current: Record<string, CsvHeaderStatus | undefined>,
    ) => Record<string, CsvHeaderStatus | undefined>,
  ) => void;
  setCsvHeaderReasonByName: (
    updater: (
      current: Record<string, string | null | undefined>,
    ) => Record<string, string | null | undefined>,
  ) => void;
  setFileValidationStatusByName: (
    updater: (
      current: Record<string, FileValidationStatus | undefined>,
    ) => Record<string, FileValidationStatus | undefined>,
  ) => void;
  setFileValidationReasonByName: (
    updater: (
      current: Record<string, string | null | undefined>,
    ) => Record<string, string | null | undefined>,
  ) => void;
  startUpload: (projectId: string, filesToUpload: File[], tags: string[]) => void;
};

const stripErrorId = (message: string): string => {
  return message.replace(/\s*\(Error ID: [^)]+\)/, '');
};

export const useResultsFileUpload = ({
  selectedProjectId,
  selectedOrganisationId,
  onRefresh,
}: ResultsFileUploadParams): ResultsFileUploadState => {
  const accessToken = useAccessToken();
  const [uploadProjectId, setUploadProjectId] = useState(selectedProjectId ?? '');
  const [uploadTagsInput, setUploadTagsInput] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [csvHeaderStatusByName, setCsvHeaderStatusByName] = useState<
    Record<string, CsvHeaderStatus | undefined>
  >({});
  const [csvHeaderReasonByName, setCsvHeaderReasonByName] = useState<
    Record<string, string | null | undefined>
  >({});
  const [fileValidationStatusByName, setFileValidationStatusByName] = useState<
    Record<string, FileValidationStatus | undefined>
  >({});
  const [fileValidationReasonByName, setFileValidationReasonByName] = useState<
    Record<string, string | null | undefined>
  >({});
  const mutationError = useMutationErrorSnackbar();
  const invalidateQueries = useInvalidateQueries();
  const { notify } = useNotifications();
  const sharedUploadInvalidations = useMemo(
    () => [
      invalidateGridQuery(gridPaths.uploads),
      invalidateGridQuery(gridPaths.resultsFiles),
      invalidateGridQuery(gridPaths.executionRuns),
      { queryKey: queryKeys.dataGrid('uploads/status-count'), exact: false },
      { queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId), exact: false },
      {
        queryKey: queryKeys.tagOptions(uploadProjectId || null, selectedOrganisationId),
        exact: false,
      },
    ],
    [selectedOrganisationId, selectedProjectId, uploadProjectId],
  );
  const invalidateUploadListQueries = useCallback(async (): Promise<void> => {
    await invalidateQueries(sharedUploadInvalidations);
  }, [invalidateQueries, sharedUploadInvalidations]);
  const invalidateUploadRelatedTables = useCallback(async (): Promise<void> => {
    await invalidateQueries([
      ...sharedUploadInvalidations,
      { queryKey: queryKeys.executionRunSummaryPrefix(), exact: false },
      { queryKey: queryKeys.metricsPrefix(), exact: false },
    ]);
  }, [invalidateQueries, sharedUploadInvalidations]);
  const selectedProjectIdRef = useRef<string | null>(selectedProjectId ?? null);
  const mutationErrorRef = useRef(mutationError);
  const csvValidationTokensRef = useRef<Record<string, number>>({});
  const fileValidationTokensRef = useRef<Record<string, number>>({});
  const uploadFilesRef = useRef<File[]>([]);
  const activeProjectId =
    selectedProjectId ?? (uploadProjectId.length > 0 ? uploadProjectId : null);
  const csvMappingQuery = useProjectCsvMappingQuery(activeProjectId, { enabled: uploadOpen });
  const resolvedHeaderAliases = useMemo<CsvHeaderAliases>(() => {
    const source = csvMappingQuery.data?.headerAliases ?? defaultCsvHeaderAliases;
    const normalizeList = (aliases: string[] | undefined): string[] =>
      (aliases ?? []).map((alias) => alias.trim().toLowerCase()).filter(Boolean);
    return {
      suite: normalizeList(source.suite),
      test: normalizeList(source.test),
      status: normalizeList(source.status),
      ...(source.duration ? { duration: normalizeList(source.duration) } : {}),
      ...(source.timestamp ? { timestamp: normalizeList(source.timestamp) } : {}),
    };
  }, [csvMappingQuery.data]);
  useEffect(() => {
    selectedProjectIdRef.current = selectedProjectId ?? null;
  }, [selectedProjectId]);
  useEffect(() => {
    mutationErrorRef.current = mutationError;
  }, [mutationError]);
  useEffect(() => {
    uploadFilesRef.current = uploadFiles;
  }, [uploadFiles]);
  useEffect(() => {
    setSelectedFiles((current) => {
      const next = new Set(current);
      uploadFilesRef.current.forEach((file) => {
        if (!isCsvResultsFile(file)) return;
        if (csvHeaderStatusByName[file.name] !== 'valid') return;
        if (next.has(file.name)) return;
        next.add(file.name);
      });
      return next;
    });
  }, [csvHeaderStatusByName, setSelectedFiles]);

  const bumpCsvValidationToken = useCallback((fileName: string): number => {
    const currentToken = csvValidationTokensRef.current[fileName] ?? 0;
    const nextToken = currentToken + 1;
    csvValidationTokensRef.current[fileName] = nextToken;
    return nextToken;
  }, []);
  const bumpFileValidationToken = useCallback((fileName: string): number => {
    const currentToken = fileValidationTokensRef.current[fileName] ?? 0;
    const nextToken = currentToken + 1;
    fileValidationTokensRef.current[fileName] = nextToken;
    return nextToken;
  }, []);

  const parseCsvLine = useCallback((line: string): string[] => {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;
    let i = 0;
    while (i < line.length) {
      const char = line[i] ?? '';
      if (inQuotes) {
        if (char === '"') {
          const next = line[i + 1];
          if (next === '"') {
            current += '"';
            i += 2;
            continue;
          }
          inQuotes = false;
          i += 1;
          continue;
        }
        current += char;
        i += 1;
        continue;
      }
      if (char === '"') {
        inQuotes = true;
        i += 1;
        continue;
      }
      if (char === ',') {
        cells.push(current);
        current = '';
        i += 1;
        continue;
      }
      current += char;
      i += 1;
    }
    cells.push(current);
    return cells;
  }, []);
  const normalizeCsvHeader = useCallback(
    (headerLine: string): string[] => {
      const stripped = headerLine.replace(/^\uFEFF/, '').trim();
      if (!stripped) return [];
      return parseCsvLine(stripped)
        .map((value) => value.trim().toLowerCase())
        .filter((value) => value.length > 0);
    },
    [parseCsvLine],
  );
  const readHeaderCells = useCallback(
    async (file: File): Promise<string[]> => {
      if (isSpreadsheetResultsFile(file)) {
        const xlsx = await import('xlsx');
        const arrayBuffer = await file.arrayBuffer();
        const workbook = xlsx.read(arrayBuffer, {
          type: 'array',
          cellDates: true,
          raw: true,
        });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) return [];
        const worksheet = workbook.Sheets[firstSheetName];
        const rows = xlsx.utils.sheet_to_json(worksheet, {
          header: 1,
          raw: true,
          blankrows: false,
          defval: '',
        }) as unknown[];
        const headerRow = rows[0];
        if (!Array.isArray(headerRow)) return [];
        return headerRow
          .map((cell) => {
            if (cell === null || cell === undefined) return '';
            if (typeof cell === 'string') return cell;
            if (typeof cell === 'number' || typeof cell === 'boolean' || typeof cell === 'bigint') {
              return String(cell);
            }
            if (cell instanceof Date) return cell.toISOString();
            return '';
          })
          .map((value) => value.trim().toLowerCase())
          .filter((value) => value.length > 0);
      }

      const chunk = await file.slice(0, 65536).text();
      const lineBreakIndex = chunk.search(/\r\n|\n|\r/);
      const firstLine = lineBreakIndex === -1 ? chunk : chunk.slice(0, lineBreakIndex);
      return normalizeCsvHeader(firstLine);
    },
    [normalizeCsvHeader],
  );
  const resolveCsvHeaderStatus = useCallback(
    async (file: File): Promise<CsvHeaderValidationResult> => {
      try {
        const headerParts = await readHeaderCells(file);
        if (headerParts.length === 0) {
          return { status: 'invalid', reason: 'Missing header row.' };
        }
        const headerSet = new Set(headerParts);
        const required = ['suite', 'test', 'status'] as const;
        const resolvedAliases = resolvedHeaderAliases;
        const missing = required.filter(
          (key) => !resolvedAliases[key].some((alias) => headerSet.has(alias)),
        );
        if (missing.length > 0) {
          return {
            status: 'invalid',
            reason: `Missing required header${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}.`,
          };
        }
        return { status: 'valid', reason: null };
      } catch {
        return { status: 'invalid', reason: 'Unable to read CSV/XLSX header.' };
      }
    },
    [readHeaderCells, resolvedHeaderAliases],
  );
  const resolveFileValidationStatus = useCallback(
    async (file: File): Promise<FileValidationResult | null> => {
      if (isJsonResultsFile(file)) {
        try {
          const content = await file.text();
          if (!content.trim()) {
            return { status: 'invalid', reason: 'JSON file is empty.' };
          }
          JSON.parse(content);
          return { status: 'valid', reason: null };
        } catch {
          return { status: 'invalid', reason: 'Invalid JSON.' };
        }
      }
      if (isXmlResultsFile(file)) {
        try {
          const content = await file.text();
          if (!content.trim()) {
            return { status: 'invalid', reason: 'XML file is empty.' };
          }
          const parser = new DOMParser();
          const doc = parser.parseFromString(content, 'application/xml');
          const parserErrors = doc.getElementsByTagName('parsererror');
          if (parserErrors.length > 0) {
            return { status: 'invalid', reason: 'Invalid XML.' };
          }
          return { status: 'valid', reason: null };
        } catch {
          return { status: 'invalid', reason: 'Invalid XML.' };
        }
      }
      return null;
    },
    [],
  );
  const revalidateCsvFiles = useCallback(() => {
    const csvFiles = uploadFilesRef.current.filter((file) => isCsvResultsFile(file));
    if (csvFiles.length === 0) return;
    setCsvHeaderStatusByName((current) => {
      const next = { ...current };
      csvFiles.forEach((file) => {
        next[file.name] = 'valid';
      });
      return next;
    });
    setCsvHeaderReasonByName((current) => {
      const next = { ...current };
      csvFiles.forEach((file) => {
        next[file.name] = null;
      });
      return next;
    });
    setSelectedFiles((current) => {
      const next = new Set(current);
      csvFiles.forEach((file) => {
        next.add(file.name);
      });
      return next;
    });
    csvFiles.forEach((file) => {
      const token = bumpCsvValidationToken(file.name);
      void resolveCsvHeaderStatus(file).then((result) => {
        setCsvHeaderStatusByName((current) => {
          if (csvValidationTokensRef.current[file.name] !== token) return current;
          const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
          if (!stillPresent) return current;
          return { ...current, [file.name]: result.status };
        });
        setSelectedFiles((current) => {
          if (csvValidationTokensRef.current[file.name] !== token) return current;
          const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
          if (!stillPresent) return current;
          const next = new Set(current);
          if (result.status === 'invalid') {
            next.delete(file.name);
            return next;
          }
          next.add(file.name);
          return next;
        });
        setCsvHeaderReasonByName((current) => {
          if (csvValidationTokensRef.current[file.name] !== token) return current;
          const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
          if (!stillPresent) return current;
          const fallbackReason =
            result.status === 'invalid' ? 'Missing required headers: suite, test, status.' : null;
          return { ...current, [file.name]: result.reason ?? fallbackReason };
        });
      });
    });
  }, [
    activeProjectId,
    bumpCsvValidationToken,
    csvMappingQuery.data,
    csvMappingQuery.isPending,
    resolveCsvHeaderStatus,
    setCsvHeaderReasonByName,
  ]);
  useEffect(() => {
    if (!uploadOpen) return;
    revalidateCsvFiles();
  }, [activeProjectId, revalidateCsvFiles, uploadOpen]);

  const pollResultsFile = useResultsFileUploadPolling({
    accessToken,
    selectedOrganisationId,
    onRefresh,
    invalidateUploadRelatedTables,
  });
  const resolveSource = useCallback(resolveResultsFileSource, []);
  const isSupportedFile = useCallback(
    (file: File) => {
      if (!isSupportedResultsFile(file)) return false;
      if (isCsvResultsFile(file)) {
        const headerStatus = csvHeaderStatusByName[file.name];
        return headerStatus !== 'invalid';
      }
      if (isJsonResultsFile(file) || isXmlResultsFile(file)) {
        const status = fileValidationStatusByName[file.name];
        return status !== 'invalid';
      }
      return true;
    },
    [csvHeaderStatusByName, fileValidationStatusByName],
  );
  const handleFilesSelected = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      setUploadFiles((current) => {
        const byName = new Map(current.map((file) => [file.name, file]));
        files.forEach((file) => {
          byName.set(file.name, file);
        });
        return Array.from(byName.values());
      });
      setSelectedFiles((current) => {
        const next = new Set(current);
        files.forEach((file) => {
          next.add(file.name);
        });
        return next;
      });
      const csvFiles = files.filter((file) => isCsvResultsFile(file));
      if (csvFiles.length > 0) {
        setCsvHeaderStatusByName((current) => {
          const next = { ...current };
          csvFiles.forEach((file) => {
            next[file.name] = 'valid';
          });
          return next;
        });
        setSelectedFiles((current) => {
          const next = new Set(current);
          csvFiles.forEach((file) => {
            next.add(file.name);
          });
          return next;
        });
        setCsvHeaderReasonByName((current) => {
          const next = { ...current };
          csvFiles.forEach((file) => {
            next[file.name] = null;
          });
          return next;
        });
        csvFiles.forEach((file) => {
          const token = bumpCsvValidationToken(file.name);
          void resolveCsvHeaderStatus(file).then((result) => {
            setCsvHeaderStatusByName((current) => {
              if (csvValidationTokensRef.current[file.name] !== token) return current;
              const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
              if (!stillPresent) return current;
              return { ...current, [file.name]: result.status };
            });
            setSelectedFiles((current) => {
              if (csvValidationTokensRef.current[file.name] !== token) return current;
              const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
              if (!stillPresent) return current;
              const next = new Set(current);
              if (result.status === 'invalid') {
                next.delete(file.name);
                return next;
              }
              next.add(file.name);
              return next;
            });
            setCsvHeaderReasonByName((current) => {
              if (csvValidationTokensRef.current[file.name] !== token) return current;
              const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
              if (!stillPresent) return current;
              const fallbackReason =
                result.status === 'invalid'
                  ? 'Missing required headers: suite, test, status.'
                  : null;
              return { ...current, [file.name]: result.reason ?? fallbackReason };
            });
          });
        });
      }

      const jsonXmlFiles = files.filter(
        (file) => isJsonResultsFile(file) || isXmlResultsFile(file),
      );
      if (jsonXmlFiles.length === 0) return;
      setFileValidationStatusByName((current) => {
        const next = { ...current };
        jsonXmlFiles.forEach((file) => {
          next[file.name] = 'valid';
        });
        return next;
      });
      setFileValidationReasonByName((current) => {
        const next = { ...current };
        jsonXmlFiles.forEach((file) => {
          next[file.name] = null;
        });
        return next;
      });
      jsonXmlFiles.forEach((file) => {
        const token = bumpFileValidationToken(file.name);
        void resolveFileValidationStatus(file).then((result) => {
          if (!result) return;
          setFileValidationStatusByName((current) => {
            if (fileValidationTokensRef.current[file.name] !== token) return current;
            const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
            if (!stillPresent) return current;
            return { ...current, [file.name]: result.status };
          });
          setSelectedFiles((current) => {
            if (fileValidationTokensRef.current[file.name] !== token) return current;
            const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
            if (!stillPresent) return current;
            const next = new Set(current);
            if (result.status === 'invalid') {
              next.delete(file.name);
              return next;
            }
            next.add(file.name);
            return next;
          });
          setFileValidationReasonByName((current) => {
            if (fileValidationTokensRef.current[file.name] !== token) return current;
            const stillPresent = uploadFilesRef.current.some((item) => item.name === file.name);
            if (!stillPresent) return current;
            return { ...current, [file.name]: result.reason ?? null };
          });
        });
      });
    },
    [
      bumpCsvValidationToken,
      bumpFileValidationToken,
      resolveCsvHeaderStatus,
      resolveFileValidationStatus,
    ],
  );

  const selectedUploadFiles = useMemo(
    () => uploadFiles.filter((file) => selectedFiles.has(file.name) && isSupportedFile(file)),
    [isSupportedFile, selectedFiles, uploadFiles],
  );
  const uploadTagValues = useMemo(
    () =>
      Array.from(
        new Set(
          uploadTagsInput
            .split(',')
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0),
        ),
      ),
    [uploadTagsInput],
  );
  const closeUploadDialog = useCallback(() => {
    setUploadOpen(false);
    setUploadError(null);
    setUploadProjectId(selectedProjectIdRef.current ?? '');
    setUploadTagsInput('');
    setUploadFiles([]);
    setSelectedFiles(new Set());
    setCsvHeaderStatusByName({});
    setCsvHeaderReasonByName({});
    setFileValidationStatusByName({});
    setFileValidationReasonByName({});
    csvValidationTokensRef.current = {};
    fileValidationTokensRef.current = {};
    mutationErrorRef.current.clearError();
  }, []);
  const openUploadDialog = useCallback(() => {
    setUploadOpen(true);
    setUploadError(null);
    mutationErrorRef.current.clearError();
    if (selectedProjectIdRef.current) {
      setUploadProjectId(selectedProjectIdRef.current);
    }
  }, []);
  const uploadMutation = useMutation<
    UploadMutationData,
    unknown,
    { files: File[]; projectId: string; tags: string[] }
  >({
    mutationFn: async ({ files, projectId, tags }) => {
      let count = 0;
      const results: Array<{
        fileName: string;
        uploadId?: string;
        resultsFileSlug?: string | null;
        error?: string;
      }> = [];
      for (const file of files) {
        let uploadId: string | undefined;
        try {
          const createResponse = await createUpload(
            projectId,
            {
              source: resolveSource(file),
              fileName: file.name,
              contentType: file.type || 'application/octet-stream',
              tags: tags.length > 0 ? tags : undefined,
            },
            selectedOrganisationId,
            accessToken,
          );
          uploadId = createResponse.uploadId;
          markUploadToastIntent(uploadId);
          await uploadFile(createResponse.uploadId, file, selectedOrganisationId, accessToken);
          const committed = await commitUpload(
            createResponse.uploadId,
            selectedOrganisationId,
            accessToken,
          );
          results.push({
            fileName: file.name,
            uploadId,
            resultsFileSlug: committed.resultsFileSlug ?? null,
          });
          count += 1;
        } catch (err) {
          const reason = getErrorMessage(err);
          results.push({
            fileName: file.name,
            uploadId,
            error: stripErrorId(reason),
          });
        }
      }
      return { count, results };
    },
    onSuccess: async (result) => {
      setUploadOpen(false);
      setUploadFiles([]);
      setSelectedFiles(new Set());
      setUploadTagsInput('');
      setUploadError(null);
      setCsvHeaderStatusByName({});
      setCsvHeaderReasonByName({});
      setFileValidationStatusByName({});
      setFileValidationReasonByName({});
      csvValidationTokensRef.current = {};
      fileValidationTokensRef.current = {};
      mutationError.clearError();
      await invalidateUploadListQueries();
      result.results.forEach((entry) => {
        if (entry.error) {
          const failureHref = entry.uploadId ? `/uploads/${entry.uploadId}` : null;
          notify({
            message: (
              <Inline gap="sm" align="center">
                <Text role="body" component="span">
                  {entry.fileName}: {entry.error}
                </Text>
                {failureHref ? (
                  <Link component={RouterLink} to={failureHref} underline="always" color="inherit">
                    View
                  </Link>
                ) : null}
              </Inline>
            ),
            tone: 'red',
          });
          return;
        }
        if (entry.uploadId) {
          pollResultsFile(entry.uploadId);
        }
      });
      onRefresh?.();
    },
    onError: (err) => {
      mutationError.setError(err);
    },
  });
  const disableUpload =
    uploadFiles.length === 0 ||
    selectedUploadFiles.length === 0 ||
    (!selectedProjectId && !uploadProjectId);

  const startUpload = useCallback(
    (projectId: string, filesToUpload: File[], tags: string[]) => {
      const fileLabel = filesToUpload.length === 1 ? 'file' : 'files';
      notify({ message: `Uploading ${filesToUpload.length} ${fileLabel}...`, tone: 'blue' });
      uploadMutation.mutate({ files: filesToUpload, projectId, tags });
    },
    [notify, uploadMutation],
  );
  return {
    uploadOpen,
    uploadProjectId,
    uploadTagsInput,
    uploadTagValues,
    uploadFiles,
    selectedFiles,
    uploadError,
    selectedUploadFiles,
    uploadMutation,
    disableUpload,
    isSupportedFile,
    csvHeaderStatusByName,
    csvHeaderReasonByName,
    fileValidationStatusByName,
    fileValidationReasonByName,
    handleFilesSelected,
    openUploadDialog,
    closeUploadDialog,
    setUploadProjectId,
    setUploadTagsInput,
    setUploadFiles,
    setSelectedFiles,
    setUploadError,
    setCsvHeaderStatusByName,
    setCsvHeaderReasonByName,
    setFileValidationStatusByName,
    setFileValidationReasonByName,
    startUpload,
  };
};
